import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, Divider, Badge } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { AuthContext } from '../../context/logeduser';
import { useSingleEmpLeaveDetails } from '../../hooks/useLeaves';
import { useGetEmployeePublishedPayslips } from '../../hooks/useEmployees';

const ACCENT = '#2f6bff';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtRange = (s, e) => {
  const a = new Date(s); const b = new Date(e);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()} – ${b.getDate()} ${MONTHS[b.getMonth()]}`
    : `${a.getDate()} ${MONTHS[a.getMonth()]} – ${b.getDate()} ${MONTHS[b.getMonth()]}`;
};

// Parse a date-ish value (ISO, YYYY-MM, or DD-MM-YYYY) into epoch ms so items of
// different kinds (leave dates vs payslip month/year) sort on one chronological axis.
const parseTime = (v) => {
  if (!v) return 0;
  const s = String(v);
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.getTime();
  const m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]).getTime();
  return 0;
};

// Frontend-derived notification bell: builds items from the employee's leave +
// published-payslip data (React Query refetch-on-focus ≈ near real-time), tracks
// seen/cleared keys in localStorage. Lights for new approved/declined leaves and
// newly published payslips. No backend notification system involved.
const NotificationBell = () => {
  const navigate = useNavigate();
  const { authData } = useContext(AuthContext);
  const employeeId = authData?.employee_no;
  const [anchor, setAnchor] = useState(null);

  const { data: leaveList } = useSingleEmpLeaveDetails(employeeId);
  const { data: payslipData } = useGetEmployeePublishedPayslips(employeeId);

  const notifications = useMemo(() => {
    const items = [];
    (Array.isArray(leaveList) ? leaveList : []).forEach((l) => {
      if (l.status === 'ACCEPTED' || l.status === 'DECLINED') {
        items.push({
          key: `leave-${l.id}-${l.status}`,
          kind: 'leave',
          approved: l.status === 'ACCEPTED',
          title: `${l.leave_type || 'Leave'} ${l.status === 'ACCEPTED' ? 'approved' : 'declined'}`,
          subtitle: fmtRange(l.start_date, l.end_date),
          // Sort by when it actually happened (approval/decline), as epoch ms.
          ts: parseTime(l.approved_date || l.declined_date || l.created_at),
        });
      }
    });
    (payslipData?.payslips || []).forEach((p) => {
      if (String(p.status || '').toUpperCase() === 'PUBLISHED') {
        // p.month may be a number (1-12) or a name ("May"); normalise to a number
        // so items sort chronologically, not alphabetically by month name.
        const monthNum = typeof p.month === 'number'
          ? p.month
          : (MONTHS.findIndex((m) => m.toLowerCase() === String(p.month).slice(0, 3).toLowerCase()) + 1 || 1);
        const mLabel = MONTHS[monthNum - 1] || p.month;
        items.push({
          key: `payslip-${p.year}-${p.month}`,
          kind: 'payslip',
          title: 'Payslip published',
          subtitle: `${mLabel} ${p.year}`,
          // Carried so the click can deep-link straight to this month's payslip.
          month: monthNum,
          year: p.year,
          ts: new Date(p.year, monthNum - 1, 1).getTime(),
        });
      }
    });
    // Newest first: largest timestamp at the top, oldest at the bottom.
    return items.sort((a, b) => b.ts - a.ts);
  }, [leaveList, payslipData]);

  const seenKey = `emp_notif_seen_${employeeId || 'x'}`;
  const clearedKey = `emp_notif_cleared_${employeeId || 'x'}`;
  const [seen, setSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(seenKey) || '[]'); } catch { return []; }
  });
  const [cleared, setCleared] = useState(() => {
    try { return JSON.parse(localStorage.getItem(clearedKey) || '[]'); } catch { return []; }
  });

  const visibleNotifications = notifications.filter((n) => !cleared.includes(n.key));
  const unreadCount = visibleNotifications.filter((n) => !seen.includes(n.key)).length;

  const openNotif = (e) => {
    setAnchor(e.currentTarget);
    if (unreadCount) {
      const next = Array.from(new Set([...seen, ...visibleNotifications.map((n) => n.key)]));
      setSeen(next);
      try { localStorage.setItem(seenKey, JSON.stringify(next)); } catch { /* ignore */ }
    }
  };

  const clearAll = () => {
    const next = Array.from(new Set([...cleared, ...notifications.map((n) => n.key)]));
    setCleared(next);
    try { localStorage.setItem(clearedKey, JSON.stringify(next)); } catch { /* ignore */ }
    setAnchor(null);
  };

  return (
    <>
      <IconButton onClick={openNotif} sx={{ color: '#6b7280' }}>
        <Badge variant="dot" color="error" invisible={unreadCount === 0}>
          <NotificationsNoneIcon sx={{ fontSize: 22, color: unreadCount ? ACCENT : '#6b7280' }} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: '14px', width: 320, maxHeight: 420, boxShadow: '0 12px 32px rgba(17,24,39,0.14)', mt: 1 } }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 800, color: '#111827' }}>Notifications</Typography>
          {visibleNotifications.length > 0 && (
            <Typography
              onClick={clearAll}
              sx={{ fontSize: '0.78rem', fontWeight: 700, color: ACCENT, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Clear all
            </Typography>
          )}
        </Box>
        <Divider />
        {visibleNotifications.length ? visibleNotifications.slice(0, 10).map((n) => (
          <MenuItem
            key={n.key}
            onClick={() => {
              setAnchor(null);
              navigate(n.kind === 'payslip'
                ? `/payslip?month=${n.year}-${String(n.month).padStart(2, '0')}`
                : '/leaves');
            }}
            sx={{ py: 1.1, alignItems: 'flex-start', gap: 0.5, whiteSpace: 'normal' }}
          >
            <ListItemIcon sx={{ mt: 0.3 }}>
              {n.kind === 'payslip'
                ? <ReceiptLongIcon fontSize="small" sx={{ color: ACCENT }} />
                : n.approved
                  ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#16a34a' }} />
                  : <CancelOutlinedIcon fontSize="small" sx={{ color: '#ef4444' }} />}
            </ListItemIcon>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{n.title}</Typography>
              {n.subtitle && <Typography variant="caption" sx={{ color: '#6b7280' }}>{n.subtitle}</Typography>}
            </Box>
          </MenuItem>
        )) : (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>No notifications yet</Typography>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
